import type { ReactElement, ReactNode } from 'react';
import { HistoryOutlined } from '@ant-design/icons';
import { useTranslation } from '@govard/i18n-core/react';
import { dayKey, dayLabel, formatTime } from '../../shared/format/entity-detail.js';
import { EntityActionMenu, type EntityAction } from './EntityActionMenu.js';
import { EmptyState } from './EmptyState.js';

export function EntityHistoryFeed({
  items,
  emptyText,
  emptyAction,
}: {
  items: {
    id: string;
    text: ReactNode;
    date: string;
    icon?: ReactNode;
    meta?: ReactNode;
    actions?: EntityAction[];
  }[];
  emptyText?: string;
  emptyAction?: ReactNode;
}): ReactElement {
  const { t } = useTranslation();
  const sorted = [...items].sort((a, b) => Date.parse(b.date) - Date.parse(a.date));
  return sorted.length === 0 ? (
    <EmptyState text={emptyText ?? t('crm.entity_detail.empty_history')} action={emptyAction} />
  ) : (
    <div className="entity-history-feed">
      {sorted.map((item, index) => (
        <div key={item.id}>
          {index === 0 || dayKey(sorted[index - 1]!.date) !== dayKey(item.date) ? (
            <h3 className="entity-history-day">{dayLabel(item.date)}</h3>
          ) : null}
          <div className="entity-history-item">
            <span className="entity-history-icon">{item.icon ?? <HistoryOutlined />}</span>
            <div className="entity-row-body">
              <div className="entity-history-text">{item.text}</div>
              <time className="entity-meta" dateTime={item.date}>
                {formatTime(item.date)}
                {item.meta ? <> · {item.meta}</> : null}
              </time>
            </div>
            {item.actions?.length ? <EntityActionMenu actions={item.actions} /> : null}
          </div>
        </div>
      ))}
    </div>
  );
}
